import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlayCircle,
  Calendar,
  Mail,
  FileText,
  Clock,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Circle,
  Eye,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatResumeDate, formatResumeRoundLabel } from '../../utils/disputeWizardResume';
import { buildProgressChecklist } from '../../utils/disputeLifecycle';

function ProgressStepIcon({ state }) {
  if (state === 'complete') {
    return <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />;
  }
  if (state === 'current') {
    return (
      <span className="w-3.5 h-3.5 rounded-full border-2 border-emerald-500 bg-emerald-500/20 flex-shrink-0 inline-block" />
    );
  }
  return <Circle size={14} className="text-stone-300 dark:text-emerald-900/60 flex-shrink-0" />;
}

export default function ResumeLastDisputeCard({
  candidate,
  completedPackage,
  loading,
  resuming,
  onResume,
  onPreviewLetters,
  t,
  dw = 'disputeWizardLanding',
  compact = false,
}) {
  const navigate = useNavigate();

  if (loading) return null;

  if (!candidate && completedPackage) {
    return (
      <div
        className="rounded-2xl p-5 sm:p-6 mb-6 border border-stone-200 bg-gradient-to-br from-white via-stone-50/50 to-stone-100/30 dark:from-[#0d2018] dark:to-[#0d2018] dark:border-emerald-800/30"
        data-testid="view-completed-package-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-stone-900 dark:text-white">
              {t(`${dw}.viewCompletedTitle`)}
            </h2>
            <p className="text-sm text-stone-600 dark:text-emerald-100/80">
              {formatResumeRoundLabel(completedPackage)} · {completedPackage.letter_count || 0} letters
            </p>
          </div>
          <Button
            onClick={() => navigate('/disputes')}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
          >
            {t(`${dw}.openDisputeCenter`)}
          </Button>
        </div>
      </div>
    );
  }

  if (!candidate) return null;

  const checklist = candidate.progress_checklist || buildProgressChecklist(
    candidate.current_step || candidate.wizard_step
  );

  return (
    <div
      className="
        rounded-2xl p-5 sm:p-6 mb-6 sm:mb-8 relative overflow-hidden
        border-2 border-emerald-300/80 shadow-[0_4px_24px_rgba(16,185,129,0.14)]
        bg-gradient-to-br from-white via-emerald-50/40 to-emerald-100/30
        dark:from-[#0d2018] dark:via-[#0f241c] dark:to-[#0d2018]
        dark:border-emerald-600/40 dark:shadow-[0_4px_24px_rgba(16,185,129,0.08)]
      "
      data-testid="resume-last-dispute-card"
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-emerald-300/20 to-transparent rounded-full blur-3xl dark:from-emerald-500/10" />
      </div>

      <div className="relative z-10 flex flex-col gap-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <PlayCircle size={24} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h2 className="text-lg sm:text-xl font-bold text-stone-900 dark:text-white">
                  {t(`${dw}.resumeTitle`)}
                </h2>
                <Badge
                  variant="outline"
                  className="bg-emerald-100 text-emerald-700 border-emerald-200 font-semibold dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700/40"
                >
                  {candidate.step_name || candidate.wizard_step_label}
                </Badge>
              </div>
              <p className="text-sm text-stone-600 dark:text-emerald-100/90 mb-3">
                {t(`${dw}.resumeSubtitle`)}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-4">
                <div className="flex items-center gap-2 text-stone-700 dark:text-emerald-100">
                  <FileText size={14} className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium">{formatResumeRoundLabel(candidate)}</span>
                </div>
                <div className="flex items-center gap-2 text-stone-700 dark:text-emerald-100">
                  <Mail size={14} className="text-emerald-500 flex-shrink-0" />
                  <span>
                    {(candidate.letter_count || 0) === 1
                      ? t(`${dw}.resumeLetterCount`, { count: String(candidate.letter_count) })
                      : t(`${dw}.resumeLetterCount_plural`, { count: String(candidate.letter_count || 0) })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-stone-700 dark:text-emerald-100">
                  <Clock size={14} className="text-emerald-500 flex-shrink-0" />
                  <span>
                    {t(`${dw}.resumeCurrentStep`)}: {candidate.step_name || candidate.wizard_step_label}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-stone-700 dark:text-emerald-100">
                  <Calendar size={14} className="text-emerald-500 flex-shrink-0" />
                  <span>
                    {t(`${dw}.resumeLastUpdated`)}: {formatResumeDate(candidate.updated_at || candidate.created_at)}
                  </span>
                </div>
              </div>

              {!compact && (
                <div className="rounded-xl border border-emerald-200/70 bg-white/60 dark:bg-[#0a1812]/60 dark:border-emerald-800/30 p-3 sm:p-4">
                  <p className="text-xs uppercase tracking-wide font-semibold text-stone-500 dark:text-emerald-300 mb-2">
                    {t(`${dw}.resumeProgress`)}
                  </p>
                  <ul className="space-y-1.5">
                    {checklist.map((step) => (
                      <li
                        key={step.key}
                        className={`flex items-center gap-2 text-sm ${
                          step.state === 'current'
                            ? 'text-emerald-700 dark:text-emerald-300 font-semibold'
                            : step.state === 'complete'
                              ? 'text-stone-600 dark:text-emerald-100/80'
                              : 'text-stone-400 dark:text-emerald-100/40'
                        }`}
                      >
                        <ProgressStepIcon state={step.state} />
                        <span>{step.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:min-w-[180px]">
            <Button
              onClick={onResume}
              disabled={resuming}
              className="w-full min-h-[44px] bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold shadow-sm"
              data-testid="btn-resume-last-dispute"
            >
              {resuming ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  {t(`${dw}.resumeLoading`)}
                </>
              ) : (
                <>
                  {t(`${dw}.resumeButton`)}
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
            {!compact && (
              <>
                <Button
                  variant="outline"
                  onClick={onPreviewLetters}
                  className="w-full border-stone-300 text-stone-700 hover:bg-stone-50 dark:border-emerald-700/40 dark:text-emerald-200 dark:hover:bg-emerald-900/30"
                >
                  <Eye size={16} className="mr-2" />
                  {t(`${dw}.previewLetters`)}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/disputes')}
                  className="w-full border-stone-300 text-stone-700 hover:bg-stone-50 dark:border-emerald-700/40 dark:text-emerald-200 dark:hover:bg-emerald-900/30"
                >
                  {t(`${dw}.openDisputeCenter`)}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
